import React, { ReactNode, createContext, useContext, useState } from 'react';
import { SavedList } from '~/clientApi/savedLists.model';
import { ICreator } from '~/types';

type CreatorActionModalState = {
  creators: ICreator[];
  isAddToListModalOpen: boolean;
  isEditNoteModalOpen: boolean;
  isRemoveFromListModalOpen: boolean;
  list: SavedList | null;
};

type CreatorActionModalActions = {
  setCreators: (creators: ICreator[]) => void;
  setIsAddToListModalOpen: (isOpen: boolean) => void;
  setIsEditNoteModalOpen: (isOpen: boolean) => void;
  setIsRemoveFromListModalOpen: (isOpen: boolean) => void;
  setList: (list: SavedList) => void;
  toggleAddToListModal: () => void;
  toggleEditNoteModal: () => void;
  toggleRemoveFromListModal: () => void;
  reset: () => void;
};

type CreatorActionModalContextValue = CreatorActionModalState &
  CreatorActionModalActions;

const CreatorActionModalContext = createContext<
  CreatorActionModalContextValue | undefined
>(undefined);

const initialState: CreatorActionModalState = {
  creators: [],
  isAddToListModalOpen: false,
  isEditNoteModalOpen: false,
  isRemoveFromListModalOpen: false,
  list: null,
};

type CreatorActionModalProviderProps = {
  children: ReactNode;
  initialState?: Partial<CreatorActionModalState>;
};

export const CreatorActionModalProvider: React.FC<
  CreatorActionModalProviderProps
> = ({ children, initialState: customInitialState }) => {
  const [state, setState] = useState<CreatorActionModalState>({
    ...initialState,
    ...customInitialState,
  });

  const setCreators = (creators: ICreator[]) => {
    setState((prev) => ({ ...prev, creators }));
  };

  const setIsAddToListModalOpen = (isAddToListModalOpen: boolean) => {
    setState((prev) => ({ ...prev, isAddToListModalOpen }));
  };

  const setIsEditNoteModalOpen = (isEditNoteModalOpen: boolean) => {
    setState((prev) => ({ ...prev, isEditNoteModalOpen }));
  };

  const setIsRemoveFromListModalOpen = (isRemoveFromListModalOpen: boolean) => {
    setState((prev) => ({ ...prev, isRemoveFromListModalOpen }));
  };

  const setList = (list: SavedList) => {
    setState((prev) => ({ ...prev, list }));
  };

  const toggleAddToListModal = () => {
    setState((prev) => ({
      ...prev,
      isAddToListModalOpen: !prev.isAddToListModalOpen,
    }));
  };

  const toggleEditNoteModal = () => {
    setState((prev) => ({
      ...prev,
      isEditNoteModalOpen: !prev.isEditNoteModalOpen,
    }));
  };

  const toggleRemoveFromListModal = () => {
    setState((prev) => ({
      ...prev,
      isRemoveFromListModalOpen: !prev.isRemoveFromListModalOpen,
    }));
  };

  const reset = () => {
    setState(initialState);
  };

  const value: CreatorActionModalContextValue = {
    ...state,
    setCreators,
    setIsAddToListModalOpen,
    setIsEditNoteModalOpen,
    setIsRemoveFromListModalOpen,
    setList,
    toggleAddToListModal,
    toggleEditNoteModal,
    toggleRemoveFromListModal,
    reset,
  };

  return (
    <CreatorActionModalContext.Provider value={value}>
      {children}
    </CreatorActionModalContext.Provider>
  );
};

export const useCreatorActionModal = (): CreatorActionModalContextValue => {
  const context = useContext(CreatorActionModalContext);
  if (context === undefined) {
    throw new Error(
      'useCreatorActionModal must be used within a CreatorActionModalProvider',
    );
  }
  return context;
};
